"use client";

import { getCurrentDomain } from "@/lib/domainConfig";
import { useEffect, useState } from "react";
import ProsperAd from "./ProsperAd";
import { BillboardContainer } from "./AdComponent";

// Base component that renders different ads based on domain
function DomainAwarePlacement({
  prosperPlacement,
  alias,
  className,
  fallbackComponent,
}: {
  prosperPlacement: string;
  alias?: string;
  className?: string;
  fallbackComponent?: React.ReactNode;
}) {
  const [domain, setDomain] = useState<"gumgum" | "tcgfan" | "default">(
    "default",
  );

  useEffect(() => {
    const currentDomain = getCurrentDomain(window.location.host);
    setDomain(currentDomain);
  }, []);

  // For tcgfan.com, use Prosper ads
  if (domain === "tcgfan") {
    return (
      <ProsperAd
        placementName={prosperPlacement}
        alias={alias}
        className={className}
      />
    );
  }

  // For gumgum.gg and other domains, show fallback or nothing
  return fallbackComponent || null;
}

// Sticky placements that don't need containers
export function ProsperVerticalSticky({ alias }: { alias?: string }) {
  return (
    <DomainAwarePlacement prosperPlacement="vertical_sticky" alias={alias} />
  );
}

export function ProsperHorizontalSticky({ alias }: { alias?: string }) {
  return (
    <DomainAwarePlacement prosperPlacement="horizontal_sticky" alias={alias} />
  );
}

export function ProsperVideoSlider({ alias }: { alias?: string }) {
  return <DomainAwarePlacement prosperPlacement="video_slider" alias={alias} />;
}

// Display placements with containers
export function ProsperInContentVideo({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <BillboardContainer>
      <DomainAwarePlacement
        prosperPlacement="video"
        alias={alias}
        className={className}
      />
    </BillboardContainer>
  );
}

export function ProsperSkyscraper({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <BillboardContainer>
      <DomainAwarePlacement
        prosperPlacement="skyscraper"
        alias={alias}
        className={className}
      />
    </BillboardContainer>
  );
}

export function ProsperDoubleMPU({
  alias,
  className,
}: {
  alias?: string;
  className?: string;
}) {
  return (
    <BillboardContainer>
      <DomainAwarePlacement
        prosperPlacement="double_mpu"
        alias={alias}
        className={className}
      />
    </BillboardContainer>
  );
}

// Page-level placements
export function ProsperDesktopTakeoverPlacement({ alias }: { alias?: string }) {
  return (
    <DomainAwarePlacement prosperPlacement="desktop_takeover" alias={alias} />
  );
}

export function ProsperMobileTakeoverPlacement({ alias }: { alias?: string }) {
  return (
    <DomainAwarePlacement prosperPlacement="mobile_takeover" alias={alias} />
  );
}

// Initialize sticky placements that should be added once per page
export function ProsperPageLevelAds() {
  return (
    <>
      <ProsperVerticalSticky alias="page-vertical-sticky" />
      <ProsperHorizontalSticky alias="page-horizontal-sticky" />
    </>
  );
}
